import { CClient } from '@common/utils'
import { LazyAPIImage } from '@components/common/image'
import NavBar from '@components/common/navbar'
import CompetitionCta from '@components/competitions/competition-cta'
import AuthModal from '@containers/competitions/auth-modal.dynamic'
import Leaderboard from '@components/competitions/problems/leaderboard'
import CompetitionHeader from '@components/competitions/competition-header'
import { useSessionContext } from '@contexts/session'
import { CompetitionEventDetailsProps } from '@lib/types/competitions'
import { redirectToForGetInitialProps } from '@lib/utils/common'
import Router from 'next/router'
import { parseCookies } from 'nookies'
import { useEffect, useState } from 'react'
import date, { SYSTEM_TIMEZONE } from '@lib/utils/date.utils'
import { NextSeo } from 'next-seo'
import { competitionsListSeo } from '@lib/seo'

type Props = {
  eventSlug: string
  phaseSlug: string
  data: CompetitionEventDetailsProps
  isUpcoming: boolean
  isOngoing: boolean
  ended: boolean
  isSubmissionDone: boolean
}

const PublicLeaderboard = ({
  eventSlug,
  phaseSlug,
  data,
  isUpcoming,
  isOngoing,
  ended,
  isSubmissionDone,
}: Props) => {
  const [isNormalHeader, setIsNormalHeader] = useState(true)
  const [isScrollHeader, setIsScrollHeader] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const { loggedIn, timezone } = useSessionContext()
  const checkedTimezone = timezone ? timezone : SYSTEM_TIMEZONE

  const { slug, isRegistered, title, organizer, start, end, location } = data

  const [authRedirectionUrl, setAuthRedirectionUrl] = useState(
    `/competitions/${slug}/register`
  )

  const handleRegisterNow = () => {
    if (loggedIn) {
      Router.push(
        '/competitions/[competitionName]/register/',
        `/competitions/${slug}/register/`
      )
    } else {
      setAuthRedirectionUrl(`/competitions/${slug}/competition-leaderboard/`)
      setIsOpen(true)
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0)

    const mainContainerDiv: HTMLElement = document.querySelector(
      '#main-container'
    )
    const mainContainerPosition = mainContainerDiv.offsetTop
    const headerChangeOffset = 0.3 * mainContainerPosition

    window.addEventListener('scroll', function () {
      if (window.pageYOffset >= mainContainerPosition + headerChangeOffset) {
        setIsScrollHeader(true)
        setIsNormalHeader(false)
      }
      if (window.pageYOffset < mainContainerPosition) {
        setIsNormalHeader(true)
        setIsScrollHeader(false)
      }
    })
  }, [])

  return (
    <>
      <NextSeo
        title={`${title} Leaderboard` || competitionsListSeo.title}
        description={`${title} Leaderboard` || competitionsListSeo.description}
      />
      <NavBar />
      <AuthModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        slug={slug}
        redirectionUrl={authRedirectionUrl}
        isGradeFieldShown
        ctaText="Register now"
      />
      {isNormalHeader && (
        <CompetitionHeader
          organizer={organizer}
          title={`${title} Leaderboard`}
          startDate={start}
          endDate={end}
          slug={slug}
          timezone={checkedTimezone}
          location={location}
          isUpcoming={isUpcoming}
          isOngoing={isOngoing}
          hasEnded={ended}
          isRegistered={isRegistered}
          isSubmissionDone={isSubmissionDone}
          isLoggedIn={loggedIn}
          handleRegisterNow={handleRegisterNow}
        />
      )}

      <div className="sticky top-0 z-20 flex flex-col w-full">
        {isScrollHeader && (
          <div className="w-full shadow bg-grey-50" id="scrollHeader">
            <div className="container flex-wrap mx-auto lg:pl-10">
              <div className="flex justify-between w-full py-2">
                <div className="flex w-full">
                  <LazyAPIImage
                    className="self-center hidden object-contain w-16 h-16 p-2 m-2 bg-white border rounded md:mx-0 text-grey-500 md:block"
                    src={organizer.logo}
                    alt={organizer.title}
                  />

                  <div className="flex items-center justify-end w-full mx-3 md:mx-10 md:justify-between">
                    <h1 className="mr-10 text-2xl md:text-3xl font-600 text-orange">
                      {title} Leaderboard
                    </h1>

                    <div className="md:w-64 md:pl-1">
                      <CompetitionCta
                        loggedIn={loggedIn}
                        isUpcoming={isUpcoming}
                        isOngoing={isOngoing}
                        ended={ended}
                        isRegistered={isRegistered}
                        handleRegisterNow={handleRegisterNow}
                        isSubmissionDone={isSubmissionDone}
                        slug={slug}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div id="main-container">
        <Leaderboard eventSlug={eventSlug} phaseSlug={phaseSlug} />
      </div>
    </>
  )
}

PublicLeaderboard.getInitialProps = async (context) => {
  const {
    query: { competitionName },
  } = context

  const token = parseCookies(context).token

  const cclientConfig = {
    method: 'GET',
    headers: {
      Authorization: token ? `Token ${token}` : null,
      'Content-Type': 'application/json',
    },
  }

  const response = await CClient(`/api/v1/competitions/${competitionName}/`, {
    headers: {
      Authorization: token ? `Token ${token}` : null,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    redirectToForGetInitialProps(context, `/${response.status}`)
    return {}
  }

  const competitionResponse = await CClient(
    `/api/v1/competitions/${competitionName}/`,
    cclientConfig
  )
  if (!competitionResponse.ok) {
    redirectToForGetInitialProps(context, `/${competitionResponse.status}/`)
    return {}
  }

  const result: CompetitionEventDetailsProps = await response.json()
  const results = await competitionResponse.json()

  const { start, end, phases } = results
  const isUpcoming = date(start).isAfter(date())
  const isOngoing = date().isBetween(start, end)
  const ended = date(end).isBefore(date())
  const isSubmissionDone = phases?.[0]?.linkedEntity?.hasUserSubmitted
  const phaseSlug = phases?.[0]?.slug
  const { slug } = result

  return {
    eventSlug: slug,
    phaseSlug,
    data: results,
    isUpcoming,
    isOngoing,
    ended,
    isSubmissionDone,
  }
}

export default PublicLeaderboard
